import { useState, useCallback, useEffect, useRef } from 'react';
import { useStreamChat } from '../contexts/StreamChatContext';
import { apiCall } from '../../services/apiUtil';
import { splitDisplayName } from '../../utils/helpers';

export const useDirectMessages = () => {
  const { chatClient, user: currentUser } = useStreamChat();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const hasRefreshedProfilesRef = useRef(false);

  // Fetch all DM conversations for the current user
  const fetchConversations = useCallback(async () => {
    if (!chatClient || !currentUser) {
      setLoading(false);
      return { success: false, error: 'Chat not initialized' };
    }

    try {
      setLoading(true);
      setError(null);

      // Query all messaging channels where user is a member
      const filter = {
        type: 'messaging',
        members: { $in: [String(currentUser.id)] }
      };

      const sort = [{ last_message_at: -1 }];

      const channels = await chatClient.queryChannels(filter, sort, {
        watch: true,
        state: true,
        limit: 50
      });

      // Filter to only DM channels (ID starts with 'dm-')
      const dmChannels = channels.filter(channel => channel.id?.startsWith('dm-'));

      // Transform channels to conversation format
      const formattedConversations = dmChannels.map(channel => {
        // Get the other user in the DM
        const members = Object.values(channel.state.members);
        const otherMember = members.find(m => m.user_id !== String(currentUser.id));
        const otherUser = otherMember?.user || {};

        // Get last message
        const lastMessage = channel.state.messages?.[channel.state.messages.length - 1];

        return {
          id: channel.id,
          cid: channel.cid,
          channel,
          otherUser: {
            id: otherUser.id,
            name: otherUser.name || `User ${otherUser.id}`,
            ...splitDisplayName(otherUser.name),
            profileImage: otherUser.image,
            image: otherUser.image,
            online: otherUser.online
          },
          lastMessage: lastMessage ? {
            text: lastMessage.text,
            createdAt: lastMessage.created_at,
            userId: lastMessage.user?.id
          } : null,
          unreadCount: channel.countUnread(),
          updatedAt: channel.data?.last_message_at || channel.data?.created_at
        };
      });

      setConversations(formattedConversations);

      // Calculate total unread count
      const totalUnread = formattedConversations.reduce((sum, conv) => sum + conv.unreadCount, 0);
      setUnreadCount(totalUnread);

      // One-time per session: refresh all channel members' Stream profiles with correct image URLs.
      // Clears stale presigned S3 URLs that old backend code may have stored in Stream.
      if (!hasRefreshedProfilesRef.current && formattedConversations.length > 0) {
        hasRefreshedProfilesRef.current = true;
        const otherUserIds = [...new Set(
          formattedConversations.map(c => c.otherUser?.id).filter(Boolean).map(String)
        )];
        if (otherUserIds.length > 0) {
          try {
            const { users: freshUsers } = await apiCall('/stream/get-user-details', {
              method: 'POST',
              body: JSON.stringify({ userIds: otherUserIds }),
            });

            if (freshUsers?.length > 0) {
              await apiCall('/stream/upsert-users', {
                method: 'POST',
                body: JSON.stringify({ users: freshUsers }),
              });
            }
          } catch (refreshErr) {
            console.warn('[useDirectMessages] Failed to refresh member profiles:', refreshErr);
          }
        }
      }

      return { success: true, data: formattedConversations };
    } catch (err) {
      console.error('Error fetching DM conversations:', err);
      setError(err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [chatClient, currentUser]);

  // Create or get a DM channel with another user
  const getOrCreateDMChannel = useCallback(async (otherUserId) => {

    if (!chatClient || !currentUser) {
      console.error('Chat not initialized', { chatClient: !!chatClient, currentUser });
      return { success: false, error: 'Chat not initialized' };
    }

    // Get current user ID - prefer from localStorage if Stream context doesn't have it
    const userStr = localStorage.getItem('socialUser');
    const localUser = userStr ? JSON.parse(userStr) : null;
    const currentUserId = currentUser.id || localUser?.id;

    if (!currentUserId || !otherUserId) {
      console.error('Missing user IDs', { currentUserId, otherUserId });
      return { success: false, error: 'Missing user IDs' };
    }

    if (String(currentUserId) === String(otherUserId)) {
      console.error('Cannot create DM with yourself');
      return { success: false, error: 'Cannot create DM with yourself' };
    }

    try {
      const result = await apiCall('/stream/create-dm-channel', {
        method: 'POST',
        body: JSON.stringify({
          userId1: currentUserId,
          userId2: otherUserId
        })
      });


      // Get the channel from Stream client
      const channel = chatClient.channel('messaging', result.channelId);
      await channel.watch();

      // Refresh conversations list
      await fetchConversations();

      return { success: true, channel, channelId: result.channelId };
    } catch (err) {
      console.error('Error creating DM channel:', err);
      return { success: false, error: err.message };
    }
  }, [chatClient, currentUser, fetchConversations]);

  // Mark a conversation as read
  const markAsRead = useCallback(async (channelId) => {
    if (!chatClient) return;

    try {
      const channel = chatClient.channel('messaging', channelId);
      await channel.markRead();

      // Update local state
      setConversations(prev => prev.map(conv =>
        conv.id === channelId ? { ...conv, unreadCount: 0 } : conv
      ));

      // Recalculate total unread
      setUnreadCount(prev => {
        const conv = conversations.find(c => c.id === channelId);
        return Math.max(0, prev - (conv?.unreadCount || 0));
      });
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  }, [chatClient, conversations]);

  // Listen for new messages to update unread count
  useEffect(() => {
    if (!chatClient) return;

    const handleNewMessage = (event) => {
      // Only handle DM channels
      if (event.channel_id?.startsWith('dm-')) {
        // Refresh conversations to get updated last message and unread count
        fetchConversations();
      }
    };

    const handleMarkRead = () => {
      // Recalculate unread count
      const totalUnread = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);
      setUnreadCount(totalUnread);
    };

    chatClient.on('message.new', handleNewMessage);
    chatClient.on('message.read', handleMarkRead);

    return () => {
      chatClient.off('message.new', handleNewMessage);
      chatClient.off('message.read', handleMarkRead);
    };
  }, [chatClient, conversations, fetchConversations]);

  // Initial load
  useEffect(() => {
    if (chatClient && currentUser) {
      fetchConversations();
    }
  }, [chatClient, currentUser, fetchConversations]);

  return {
    conversations,
    loading,
    error,
    unreadCount,
    fetchConversations,
    getOrCreateDMChannel,
    markAsRead,
    refreshConversations: fetchConversations
  };
};

export default useDirectMessages;
